import { DeepObjectValueComparer } from "../Comparers/DeepObjectValueComparer";
import { AndExpression } from "../Expressions/AndExpression";
import { ComparisonExpression, ComparisonExpressionOperatorEnum } from "../Expressions/ComparisonExpression";
import { Expression } from "../Expressions/Expression";
import { NotExpression } from "../Expressions/NotExpression";
import { OrExpression } from "../Expressions/OrExpression";
import { Nullable, PartialNullable, RadPromiseLike } from "../Extensions";
import { ArrayHelper } from "../Helpers/ArrayHelper";
import { DateHelper } from "../Helpers/DateHelper";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { PropertyChange } from "../PropertyChange";
import { Property } from "../RadObject";
import { AjaxRequest } from "./AjaxRequest";
import { IAjaxRequest } from "./IAjaxRequest";
import { IODataBatchRequest } from "./IODataBatchRequest";
import { IODataOptions } from "./IODataOptions";
import { IWebApiClient } from "./IWebApiClient";
import { ODataBatchRequest } from "./ODataBatchRequest";
import { ODataHelper } from "./ODataHelper";
import { WebApiClientPropertyValues } from "./WebApiClientPropertyValues";
import { WebClient } from "./WebClient";
import { NAMESPACE } from "./_Namespace";

class SelectExpandInfo
{
    public static fromColumnList(propertyList: string[])
    {
        const
            result = new SelectExpandInfo();

        let
            currentResultPart: SelectExpandInfo;

        function addExpand(name: string)
        {
            let
                expand = ArrayHelper.singleOrDefault(currentResultPart.expands, expand => expand.name === name);

            if (!expand)
            {
                expand = new SelectExpandInfo(name);
                currentResultPart.expands.push(expand);
            }

            return expand;
        }

        ArrayHelper.orderBy(propertyList, item => item)
            .forEach((path: string) =>
            {
                const
                    pathParts = path.split(".");

                currentResultPart = result;

                pathParts.forEach((pathPart: string, index: number) =>
                {
                    if (pathPart === "@odata" && index > 0 && pathParts.length > index + 1)
                    {
                        const
                            expand = pathParts[index - 1],
                            option = pathParts[index + 1];

                        switch (option)
                        {
                            case "count":
                                currentResultPart.options.count = true;
                                break;
                        }

                        return false;
                    }

                    if (index < pathParts.length - 1)
                        currentResultPart = addExpand(pathPart);
                    else
                        currentResultPart.selects.push(pathPart);
                });
            });

        return result;
    }

    public name: Nullable<string>;
    public selects: string[] = [];
    public expands: SelectExpandInfo[] = [];
    public options: { count?: boolean } = {};

    constructor(name?: string)
    {
        this.name = name !== undefined ? name : null;
    }

    public convertToQuery(): { select: string; expand: string; }
    {
        const
            select = this.selects.join(","),
            expand = this.expands.map(item => item.convertToQueryInternal()).join(",");

        return {
            select: select,
            expand: expand
        };
    }

    private convertToQueryInternal(): string
    {
        const
            select = this.selects.join(","),
            expand = this.expands.map(item => item.convertToQueryInternal()).join(",");

        let
            result = "(";

        if (this.options.count)
        {
            result += `$count=true`;

            if (!select.length && !expand.length)
                result += `;$top=0`;
        }

        if (select)
        {
            if (result.length > 1)
                result += ";";

            result += `$select=${select}`;
        }

        if (expand)
        {
            if (result.length > 1)
                result += ";";

            result += `$expand=${expand}`;
        }

        result += ")";

        return this.name + result;
    }
}

export class WebApiClient extends WebClient implements IWebApiClient
{
    public static override classNamespace = NAMESPACE + "WebApiClient";
    public static override className = "WebApiClient";

    // #region Properties

    public onAuthenticatedAsync!: Property<(data: any) => RadPromiseLike<any>>;
    public onUnauthenticated!: Property<() => void>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<WebApiClientPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public createApiMethodRequest(apiName: string, methodName: string, data?: any, additionalHeaders?: any, showLoading?: boolean, dontQueueFailedAuthenticationCall?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return new AjaxRequest({
            settings: {
                type: "POST",
                headers: ObjectHelper.extend(true, {}, this._createDefaultHeaders(), additionalHeaders),
                data: data ? ObjectHelper.toJson(data) : null,
                dataType: "json",
                contentType: "application/json",
                url: `${this.baseUrl.get()}api/${apiName}/${methodName}`,
                showLoading: showLoading === true || showLoading === false ? showLoading : true,
                displayExceptions: displayExceptions === true || displayExceptions === false ? displayExceptions : true,
                dontQueueFailedAuthenticationCall,
            },
            type: "API",
            webClient: this
        });
    }
    public createBatchRequest(requests: IAjaxRequest[], additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IODataBatchRequest
    {
        const
            baseUrl = this.baseUrl.get();

        return new ODataBatchRequest({
            settings: {
                type: "POST",
                headers: ObjectHelper.extend(true, {}, this._createDefaultHeaders(), additionalHeaders),
                url: baseUrl + "odata/$batch",
                showLoading: showLoading === true || showLoading === false ? showLoading : true,
                displayExceptions: displayExceptions === true || displayExceptions === false ? displayExceptions : true,
            },
            baseUrl: baseUrl,
            requests: requests,
            webClient: this
        });
    }
    public createEntityDeleteRequest(entitySetName: string, id: string, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return this.createODataMethodRequest(
            `${entitySetName}(${id})`,
            "DELETE",
            null,
            undefined,
            additionalHeaders,
            showLoading,
            displayExceptions,
        );
    }
    public createEntityGetRequest(entitySetName: string, id: string, columns?: Nullable<string[]>, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return this.createODataMethodRequest(
            `${entitySetName}(${id})`,
            "GET",
            null,
            {
                columns: columns
            },
            additionalHeaders,
            showLoading,
            displayExceptions,
        );
    }
    public createEntityListChildrenRequest(entitySetName: string, id: string, path: string, options?: IODataOptions, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return this.createODataMethodRequest(
            `${entitySetName}(${id})/${path}`,
            "GET",
            null,
            {
                ...options,
                count: true
            },
            additionalHeaders,
            showLoading,
            displayExceptions,
        );
    }
    public createEntitySaveRequest(entitySetName: string, entity: any, originalEntity?: any, additionalHeaders?: any, showLoading?: boolean): IAjaxRequest
    {
        let
            path = entitySetName,
            type = "POST",
            data: any;

        if (originalEntity?.rowVersion)
        {
            path = `${entitySetName}(${originalEntity.id})`;
            type = "PATCH";

            data = {};

            ObjectHelper.copySimpleProperties(entity, data, property =>
            {
                return DeepObjectValueComparer.Default.compare(originalEntity[property] ?? null, entity[property] ?? null) !== 0;
            });

            data.id = originalEntity.id;
            data.rowVersion = originalEntity.rowVersion;
        }
        else
        {
            data = {};
            ObjectHelper.copySimpleProperties(entity, data);
        }

        return this.createODataMethodRequest(
            path,
            type,
            data,
            undefined,
            ObjectHelper.extend({ "Prefer": "return=representation" }, additionalHeaders),
            showLoading
        );
    }
    public createEntitySearchRequest(entitySetName: string, options?: IODataOptions, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return this.createODataMethodRequest(
            entitySetName,
            "GET",
            null,
            {
                ...options,
                count: true,
            },
            additionalHeaders,
            showLoading,
            displayExceptions,
        );
    }
    public createEntitySetSaveRequests(entitySetName: string, currentSet: any[], originalSet: any[], additionalHeaders?: any, showLoading?: boolean): IAjaxRequest[]
    {
        const
            requests: IAjaxRequest[] = [];

        if (originalSet.length)
        {
            originalSet
                .filter(item => !currentSet.some(itemInner => itemInner.id === item.id))
                .forEach(item =>
                {
                    requests.push(
                        this.createEntityDeleteRequest(entitySetName, item.id)
                    );
                });
        }

        currentSet
            .forEach(item =>
            {
                const
                    originalRecord = ArrayHelper.firstOrDefault(originalSet, itemInner => itemInner.id === item.id);

                requests.push(
                    this.createEntitySaveRequest(entitySetName, item, originalRecord, additionalHeaders, showLoading)
                );
            });

        return requests;
    }
    public createFileUploadRequest(apiName: string, methodName: string, data: any, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        return new AjaxRequest({
            settings: {
                type: "POST",
                headers: ObjectHelper.extend(true, {}, this._createDefaultHeaders(), additionalHeaders),
                data: data,
                url: `${this.baseUrl.get()}api/${apiName}/${methodName}`,
                cache: false,
                contentType: false,
                processData: false,
                showLoading: showLoading === true || showLoading === false ? showLoading : true,
                displayExceptions: displayExceptions === true || displayExceptions === false ? displayExceptions : true,
            },
            type: "API",
            webClient: this
        });
    }
    public createODataMethodRequest(path: string, type: string, data: any, options?: IODataOptions, additionalHeaders?: any, showLoading?: boolean, displayExceptions?: boolean): IAjaxRequest
    {
        let
            optionsString = StringHelper.empty;

        if (options)
        {
            if (options.filter instanceof Expression)
                optionsString = this._addQueryStringOption(optionsString, "filter", this._convertExpressionToODataFilter(options.filter));
            else if (options.filter)
                optionsString = this._addQueryStringOption(optionsString, "filter", options.filter);

            optionsString = this._addQueryStringOptions(optionsString, "orderBy", options.orderBy);

            if (options.columns)
            {
                const
                    selectExpandInfo = SelectExpandInfo.fromColumnList(options.columns).convertToQuery();

                optionsString = this._addQueryStringOption(optionsString, "select", selectExpandInfo.select);
                optionsString = this._addQueryStringOption(optionsString, "expand", selectExpandInfo.expand);
            }

            optionsString = this._addQueryStringOption(optionsString, "skip", options.skip);
            optionsString = this._addQueryStringOption(optionsString, "top", options.take);
            optionsString = this._addQueryStringOption(optionsString, "count", options.count === true);
            optionsString = this._addQueryStringOptions(optionsString, "apply", options.apply);
            optionsString = this._addQueryStringOptions(optionsString, "compute", options.computedProperties?.map(item => `${item.formula} as ${item.name}`).join(","));
        }

        return new AjaxRequest({
            settings: {
                type: type,
                headers: ObjectHelper.extend(true, {}, this._createDefaultHeaders(), additionalHeaders),
                data: data ? ObjectHelper.toJson(data) : null,
                dataType: "json",
                contentType: "application/json",
                url: `${this.baseUrl.get()}odata/${path}${(optionsString !== StringHelper.empty ? `?${optionsString}` : StringHelper.empty)}`,
                showLoading: showLoading === true || showLoading === false ? showLoading : true,
                displayExceptions: displayExceptions === true || displayExceptions === false ? displayExceptions : true,
            },
            type: "ODATA",
            webClient: this
        });
    }

    // #endregion

    // #region Protected Methods

    protected _addQueryStringOption(optionString: string, name: string, value: any)
    {
        if ((typeof value === "number" && !ObjectHelper.isNullOrUndefined(value)) || !!value)
            optionString += `${(optionString.length ? "&" : StringHelper.empty)}$${name}=${encodeURIComponent(value)}`;

        return optionString;
    }
    protected _addQueryStringOptions(optionsString: string, optionName: string, values: Nullable<string | string[]> | undefined)
    {
        if (values)
        {
            if (Array.isArray(values))
            {
                let
                    csValues = StringHelper.empty;

                for (let value of values)
                    csValues += (csValues.length ? ", " : StringHelper.empty) + ODataHelper.objectPropertyPathToODataPropertyPath(value);

                optionsString = this._addQueryStringOption(optionsString, optionName, csValues);
            }
            else
                optionsString = this._addQueryStringOption(optionsString, optionName, ODataHelper.objectPropertyPathToODataPropertyPath(values));
        }

        return optionsString;
    }
    protected _convertExpressionToODataFilter(expression: Expression | string)
    {
        let
            filter = StringHelper.empty;

        if (typeof expression === "string")
        {
            filter = expression.trim();
            return filter;
        }

        if (expression instanceof ComparisonExpression)
        {
            const
                oDataPropertyPath = ODataHelper.objectPropertyPathToODataPropertyPath(expression.path.get() ?? undefined),
                value = expression.value.get(),
                valueType = ObjectHelper.typeOf(value);

            let
                oDataValue;

            if (value === null)
                oDataValue = "null";
            else
            {
                switch (valueType)
                {
                    case "boolean":
                        oDataValue = value ? "true" : "false";
                        break;
                    case "date":
                        {
                            const
                                dateValue = value as Date;

                            if (dateValue.getHours() + dateValue.getMinutes() + dateValue.getSeconds() + dateValue.getMilliseconds() === 0)
                                oDataValue = DateHelper.toString(value, "yyyy-MM-dd");
                            else
                                oDataValue = DateHelper.toString(value, "yyyy-MM-ddTHH:mm:ss.fffffffZ");
                            break;
                        }
                    case "number":
                        oDataValue = value.toString();
                        break;
                    case "string":
                        oDataValue = StringHelper.isGuid(value) ? value : `'${value.replace(/'/gi, "''")}'`;
                        break;
                    default:
                        throw new Error(`Value type '${valueType}' not supported`);
                }
            }

            const
                operator = expression.operator.get();

            switch (operator)
            {
                case ComparisonExpressionOperatorEnum.contains:
                    filter = `indexof(${oDataPropertyPath}, ${(oDataValue.startsWith("'") ? oDataValue : `'${oDataValue}'`)}) ge 0`;
                    break;
                case ComparisonExpressionOperatorEnum.equals:
                    filter = `${oDataPropertyPath} eq ${oDataValue}`;
                    break;
                case ComparisonExpressionOperatorEnum.greaterThan:
                    filter = `${oDataPropertyPath} gt ${oDataValue}`;
                    break;
                case ComparisonExpressionOperatorEnum.greaterThanOrEqualTo:
                    filter = `${oDataPropertyPath} ge ${oDataValue}`;
                    break;
                case ComparisonExpressionOperatorEnum.lessThan:
                    filter = `${oDataPropertyPath} lt ${oDataValue}`;
                    break;
                case ComparisonExpressionOperatorEnum.lessThanOrEqualTo:
                    filter = `${oDataPropertyPath} le ${oDataValue}`;
                    break;
                case ComparisonExpressionOperatorEnum.notEqualTo:
                    filter = `${oDataPropertyPath} ne ${oDataValue}`;
                    break;
                default:
                    throw new Error("Expression operator not supported");
            }

            return filter;
        }

        if (expression instanceof AndExpression)
        {
            expression.expressions.get()?.forEach(item =>
            {
                const
                    oDataFilter = this._convertExpressionToODataFilter(item);

                if (oDataFilter)
                    filter += `${(filter.length ? " and " : StringHelper.empty)}(${oDataFilter})`;
            });

            return filter;
        }

        if (expression instanceof OrExpression)
        {
            expression.expressions.get()?.forEach(item =>
            {
                const
                    oDataFilter = this._convertExpressionToODataFilter(item);

                if (oDataFilter)
                    filter += `${(filter.length ? " or " : StringHelper.empty)}(${oDataFilter})`;
            });

            return filter;
        }

        if (expression instanceof NotExpression)
        {
            const
                oDataFilter = this._convertExpressionToODataFilter(expression.expression.get() ?? StringHelper.empty);

            if (oDataFilter)
                filter += `not (${this._convertExpressionToODataFilter(oDataFilter)})`;

            return filter;
        }

        throw new Error("Expression type not supported");
    }
    protected _onAuthenticatedAsyncChanged(change: PropertyChange<(data: any) => RadPromiseLike<any>>)
    {
        /* Intentionally empty */
    }
    protected _onUnauthenticatedChanged(change: PropertyChange<() => void>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: WebApiClientPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.onAuthenticatedAsync = new Property<(data: any) => RadPromiseLike<any>>(this, "System.Object, System.Private.CoreLib", propertyValues.onAuthenticatedAsync, this._onAuthenticatedAsyncChanged);
        this.onUnauthenticated = new Property<() => void>(this, "System.Object, System.Private.CoreLib", propertyValues.onUnauthenticated, this._onUnauthenticatedChanged);
    }

    protected override _createDefaultHeaders()
    {
        return ObjectHelper.extend(true, {}, ObjectHelper.cloneObject(WebApiClient.staticHeaders), this._createAuthenticationHeaders());
    }

    // #endregion
}